// © 2013 - 2024 Tinder, Inc., ALL RIGHTS RESERVED

import React, { memo } from 'react';
import type { Handles, SliderItem } from 'react-compound-slider';
import styles from '../styles';
import { useKeyboardFocus } from '../../../utils/hooks';

type Props = {
  domain: number[];
  handle: SliderItem;
  disabled?: boolean;
  getHandleProps: Handles['getHandleProps'];
  accessibilityText?: string;
  accentColor?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaValueText?: string;
};

const Handle = ({
  domain: [min, max],
  handle: { id, value, percent },
  getHandleProps,
  accessibilityText,
  accentColor,
  ariaLabel,
  ariaLabelledBy,
  ariaValueText,
  ...restProps
}: Props) => {
  const [isFocused, , setIsFocused] = useKeyboardFocus(false);
  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const style: Record<string, string> = {
    left: `${percent}%`,
  };

  if (accentColor) {
    style['--tui-slider-accent-color'] = accentColor;
  }

  return (
    <div
      tabIndex={0}
      data-focused={isFocused ? '' : undefined}
      role="slider"
      onFocus={handleFocus}
      onBlur={handleBlur}
      aria-label={accessibilityText || ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-valuetext={ariaValueText}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      style={style}
      className={styles.handle}
      {...getHandleProps(id, restProps)}
    >
      <div className={styles.focus}></div>
      {accessibilityText ? <span hidden>{accessibilityText}</span> : undefined}
    </div>
  );
};

export default memo(Handle);
